/**
 * WP-5: Collection-to-Vehicle Compatibility Validation
 *
 * Implements the 4-step sequential AND validation:
 *   Step 1: QuickFit Sales Code Match (direct 35M code comparison)
 *   Step 2: Vehicle Type Match
 *   Step 3: ECU App Variant Compatibility (exact match: vehicle === collection)
 *   Step 4: XMC 4 Presence Match
 *
 * Legacy collections (no Vehicle Type or QuickFit Sales Code) skip steps 1-2.
 */

import { parseVariantNumber } from 'components/pages/FeatureCollectionDetails/collectionDetailsUtils'
import { is43NAlignedWith42N } from 'utils/pinRestrictions'

/**
 * Determine if a collection is a legacy collection.
 * Legacy collections have no Vehicle Type or QuickFit Sales Code.
 *
 * @param {object} collection
 * @returns {boolean}
 */
export const isLegacyCollection = (collection) => {
  const hasVehicleType =
    collection.vehicleTypes &&
    Array.isArray(collection.vehicleTypes) &&
    collection.vehicleTypes.length > 0
  const hasQFCode = Boolean(collection.mdlDbCd)
  return !hasVehicleType && !hasQFCode
}

/**
 * Normalize a vehicle subType to a canonical type for comparison.
 * 42x -> 'CHECP' (Plus Series), 43x -> 'CHEC2' (X-Series)
 */
const normalizeVehicleType = (subType) => {
  if (!subType) return null
  if (subType.startsWith('42')) return 'CHECP'
  if (subType.startsWith('43')) return 'CHEC2'
  if (['CHECP', 'CEEA+', '38N'].includes(subType)) return 'CHECP'
  if (subType === 'CHEC2' || subType === '45X') return 'CHEC2'
  return subType
}

/**
 * QuickFit Sales Code to compatible canonical vehicle types.
 * All 35M codes are valid for both Plus Series (CHECP) and X-Series (CHEC2).
 * Previously 35M-011 was restricted to CHECP and 35M-015 to CHEC2; this
 * restriction was removed per PO decision (2026-07-07).
 */
const QUICKFIT_COMPATIBLE_TYPES = {
  '35M-010': ['CHECP', 'CHEC2'],
  '35M-011': ['CHECP', 'CHEC2'],
  '35M-012': ['CHECP', 'CHEC2'],
  '35M-013': ['CHECP', 'CHEC2'],
  '35M-014': ['CHECP', 'CHEC2'],
  '35M-015': ['CHECP', 'CHEC2'],
}

/** QF codes for which a 43N post-cutoff vehicle may cross-use the sibling code (35M-011 ↔ 35M-015).
 * Note: all codes are now compatible with both Plus Series and X-Series, so this set is only
 * used for the cross-code QF matching exception, not for vehicle type expansion. */
const DATE_DEPENDENT_QF_CODES = ['35M-011', '35M-015']

/**
 * Extract the vehicle's 35M QuickFit Sales Code from its dbCodes array.
 * Returns the first entry that contains "35M", or null if not found.
 *
 * @param {string[]} dbCodes - Vehicle databook codes (e.g. ["35M-011"])
 * @returns {string|null}
 */
const getVehicle35mCode = (dbCodes) => {
  if (!dbCodes || !Array.isArray(dbCodes)) return null
  return (
    dbCodes.find((code) => typeof code === 'string' && code.includes('35M')) ||
    null
  )
}

/**
 * Determine whether a 43N post-cutoff date exception applies.
 *
 * The exception (PBI 144828 / S-96 Gap 5) allows a 43N vehicle whose 35M code
 * is one of the paired date-dependent codes (35M-011 / 35M-015) to use the
 * collection for the paired sibling code, provided the vehicle's build date
 * is on or after the cutoff.
 *
 * Note: this exception is about QF code cross-matching only — vehicle type
 * compatibility is no longer restricted by QF code (all codes support both
 * Plus Series and X-Series per PO decision 2026-07-07).
 *
 * Both the vehicle's code AND the collection's code must be in the paired set —
 * only the 35M-011 ↔ 35M-015 crossover is permitted.
 *
 * @param {string|null} vehicle35mCode - Vehicle's 35M code (from dbCodes)
 * @param {string} collectionQF - Collection's QF code (mdlDbCd)
 * @param {string} vehicleSubType - Vehicle subType (e.g. "43N")
 * @param {string} buildDate - Vehicle build date ISO string
 * @returns {boolean}
 */
const is43NCrossCodeExempted = (
  vehicle35mCode,
  collectionQF,
  vehicleSubType,
  buildDate
) => {
  const is43N = vehicleSubType?.startsWith('43')
  if (!is43N) return false
  const isCollectionDateDependent =
    DATE_DEPENDENT_QF_CODES.includes(collectionQF)
  const isVehicleDateDependent =
    vehicle35mCode !== null && DATE_DEPENDENT_QF_CODES.includes(vehicle35mCode)
  return (
    isCollectionDateDependent &&
    isVehicleDateDependent &&
    is43NAlignedWith42N(collectionQF, buildDate)
  )
}

/**
 * Extract a normalized module identifier from a module object.
 *
 * Vehicle details API responses embed module identity in a uri field
 * (e.g. "/vehicles/MU7748/modules/XMC3") with no separate id field.
 * The separate /modules endpoint returns objects that do have an id field.
 * This helper resolves the identifier from whichever field is present so
 * variant checks work regardless of which API shape populated the modules.
 *
 * @param {object} m - Module object (may have id, uri, or both)
 * @returns {string} Uppercase, whitespace-free identifier (e.g. "XMC3")
 */
const getModuleIdentifier = (m) => {
  const raw = m.id || (m.uri ? m.uri.split('/').pop() : '')
  return (raw || '').toUpperCase().replace(/\s/g, '')
}

/**
 * Validate collection compatibility with vehicle.
 * Implements 4-step sequential AND validation.
 *
 * @param {object} collection - Collection data from API
 *   Expected fields: mdlDbCd, vehicleTypes (array), varMinCompat, xmc4AppVariant,
 *   vehicleSubType (legacy field)
 * @param {object} vehicle - Vehicle data
 *   Expected fields: subType, dbCodes (array of databook codes), buildDate,
 *   modules (array with id, ecuVariant)
 * @returns {{ compatible: boolean, reason?: string, isLegacy: boolean }}
 */
export const isCollectionCompatibleWithVehicle = (collection, vehicle) => {
  if (!collection || !vehicle) {
    return {
      compatible: false,
      reason: 'Missing collection or vehicle data',
      isLegacy: false,
    }
  }

  const legacy = isLegacyCollection(collection)
  const vehicleNormalizedType = normalizeVehicleType(vehicle.subType)

  // For legacy collections, skip vehicle type check
  if (!legacy) {
    const collectionQF = collection.mdlDbCd

    // Step 1: Direct 35M code match — the vehicle's 35M code must match the
    // collection's QuickFit Sales Code (mdlDbCd).
    // Exception (PBI 144828 / S-96 Gap 5): post-cutoff 43N vehicles are aligned
    // with 42N pinouts and may use collections from the corresponding 35M code
    // even if the vehicle's own code differs (e.g. a 43N/35M-015 vehicle can use
    // a 35M-011 collection after the cutoff date, and vice-versa).
    if (collectionQF) {
      const vehicle35mCode = getVehicle35mCode(vehicle.dbCodes)
      if (vehicle35mCode) {
        const codesMatch = vehicle35mCode === collectionQF
        if (!codesMatch) {
          // Allow 43N post-cutoff exception: both the vehicle's code and the
          // collection's code must be in the paired date-dependent set
          // (35M-011 ↔ 35M-015). Only that specific crossover is permitted.
          const exempted = is43NCrossCodeExempted(
            vehicle35mCode,
            collectionQF,
            vehicle.subType,
            vehicle.buildDate
          )

          if (!exempted) {
            return {
              compatible: false,
              reason: 'QuickFit Sales Code does not match vehicle',
              isLegacy: false,
            }
          }
        }
      }
    }

    const compatibleTypes =
      collectionQF && QUICKFIT_COMPATIBLE_TYPES[collectionQF]
        ? QUICKFIT_COMPATIBLE_TYPES[collectionQF]
        : (collection.vehicleTypes || []).map(normalizeVehicleType)

    // PBI 144828 / S-96 Gap 5: post-cutoff 43N vehicles with 35M-011 or 35M-015
    // may cross-use the sibling code (35M-011 ↔ 35M-015). Since all codes are now
    // compatible with both vehicle types, this branch only needs to cover the case
    // where the vehicle QF code and collection QF code differ within the paired set.
    // Note: reaching here with a date-dependent collectionQF guarantees the vehicle
    // code is also date-dependent (Step 1 above enforced the pairing), so we only
    // need to check the collection side and the date.
    const is43N = vehicle.subType?.startsWith('43')
    const isDateDependentQF =
      collectionQF && DATE_DEPENDENT_QF_CODES.includes(collectionQF)
    if (
      is43N &&
      isDateDependentQF &&
      is43NAlignedWith42N(collectionQF, vehicle.buildDate)
    ) {
      // Post-cutoff 43N: compatible with both vehicle types — skip type check
    } else if (compatibleTypes.length > 0 && vehicleNormalizedType) {
      const vehicleTypeMatches = compatibleTypes.includes(vehicleNormalizedType)
      if (!vehicleTypeMatches) {
        return {
          compatible: false,
          reason: 'Vehicle type does not match',
          isLegacy: false,
        }
      }
    }
  }

  // ECU App Variant Compatibility (exact match: vehicle === collection)
  const vehicleModules = vehicle.modules || []

  // If vehicle module data hasn't loaded yet, skip ECU checks (don't reject)
  if (vehicleModules.length === 0) {
    return { compatible: true, isLegacy: legacy }
  }

  const rawVarMinCompat = collection.varMinCompat

  // A collection with no Minimum Diagnostic Variant cannot be verified as
  // compatible — treat it as incompatible when vehicle module data is loaded.
  // This ensures legacy collections without a variant requirement are hidden
  // by the "Only show compatible collections" filter.
  if (rawVarMinCompat == null) {
    return {
      compatible: false,
      reason: 'No App Variant compatibility information',
      isLegacy: legacy,
    }
  }

  // Parse pipe-separated varMinCompat (e.g., "0115|0115" -> XMC3="0115", XMC4="0115")
  let collectionXmc3Variant = rawVarMinCompat
  let collectionXmc4Variant = collection.xmc4AppVariant
  if (rawVarMinCompat && rawVarMinCompat.includes('|')) {
    const [xmc3Part, xmc4Part] = rawVarMinCompat.split('|')
    collectionXmc3Variant = xmc3Part
    collectionXmc4Variant = collectionXmc4Variant || xmc4Part
  }

  if (collectionXmc3Variant) {
    const xmc3Module = vehicleModules.find(
      (m) => getModuleIdentifier(m) === 'XMC3'
    )
    if (xmc3Module && xmc3Module.ecuVariant) {
      const vehicleNum = parseVariantNumber(xmc3Module.ecuVariant)
      const collectionNum = parseVariantNumber(collectionXmc3Variant)
      if (
        vehicleNum !== null &&
        collectionNum !== null &&
        vehicleNum !== collectionNum
      ) {
        return {
          compatible: false,
          reason: 'ECU app variant is incompatible',
          isLegacy: legacy,
        }
      }
    }
  }

  if (collectionXmc4Variant) {
    const xmc4Module = vehicleModules.find(
      (m) => getModuleIdentifier(m) === 'XMC4'
    )
    if (xmc4Module && xmc4Module.ecuVariant) {
      const vehicleNum = parseVariantNumber(xmc4Module.ecuVariant)
      const collectionNum = parseVariantNumber(collectionXmc4Variant)
      if (
        vehicleNum !== null &&
        collectionNum !== null &&
        vehicleNum !== collectionNum
      ) {
        return {
          compatible: false,
          reason: 'ECU app variant is incompatible',
          isLegacy: legacy,
        }
      }
    }
  }

  // XMC 4 Presence Match
  // If collection has XMC 4 data but vehicle doesn't have XMC 4 = incompatible
  const collectionHasXmc4 = Boolean(collectionXmc4Variant)
  const vehicleHasXmc4 = vehicleModules.some(
    (m) => getModuleIdentifier(m) === 'XMC4'
  )
  if (collectionHasXmc4 && !vehicleHasXmc4) {
    return {
      compatible: false,
      reason: 'Collection requires XMC 4 but vehicle does not have it',
      isLegacy: legacy,
    }
  }

  return { compatible: true, isLegacy: legacy }
}

/**
 * Format vehicle types for display.
 * When both types: use & (e.g., "Plus Series & X-Series")
 *
 * @param {string[]} vehicleTypes
 * @returns {string}
 */
export const formatVehicleTypes = (vehicleTypes) => {
  if (
    !vehicleTypes ||
    !Array.isArray(vehicleTypes) ||
    vehicleTypes.length === 0
  ) {
    return ''
  }
  return vehicleTypes.join(' & ')
}

/**
 * QuickFit Sales Code to vehicle type display string.
 * All codes now support both Plus Series and X-Series.
 */
const QUICKFIT_VEHICLE_TYPE_MAP = {
  '35M-010': 'Plus Series & X-Series',
  '35M-011': 'Plus Series & X-Series',
  '35M-012': 'Plus Series & X-Series',
  '35M-013': 'Plus Series & X-Series',
  '35M-014': 'Plus Series & X-Series',
  '35M-015': 'Plus Series & X-Series',
}

/**
 * Build the "Vehicle Type, QF Code" display string for a collection card.
 * Derives the vehicle type from the QuickFit Sales Code.
 * e.g., "Plus Series & X-Series, 35M-012" or "X-Series, 35M-015"
 *
 * Falls back to the vehicleTypes array if the QF code is unknown.
 * Returns empty string if no data is available.
 *
 * @param {string} mdlDbCd - QuickFit Sales Code (e.g., "35M-012")
 * @param {string[]} vehicleTypes - Fallback vehicle types array
 * @returns {string}
 */
export const formatVehicleTypeAndQFCode = (mdlDbCd, vehicleTypes) => {
  if (mdlDbCd && QUICKFIT_VEHICLE_TYPE_MAP[mdlDbCd]) {
    return `${QUICKFIT_VEHICLE_TYPE_MAP[mdlDbCd]}, ${mdlDbCd}`
  }
  // QF code present but not in our map — show type from array + code
  if (mdlDbCd) {
    const typeDisplay = formatVehicleTypes(vehicleTypes)
    return typeDisplay ? `${typeDisplay}, ${mdlDbCd}` : mdlDbCd
  }
  // No QF code — just show vehicle types if available
  return formatVehicleTypes(vehicleTypes)
}

/**
 * Format ECU inventory and app variants for card display.
 * When both XMC 3 and XMC 4: "XMC 3: App_xxxx | XMC 4: App_xxxx"
 * When only XMC 3: "XMC 3: App_xxxx"
 *
 * varMinCompat from API can be:
 *   - Pipe-separated suffixes: "0115|0115" (XMC3|XMC4)
 *   - Single suffix: "0115"
 *   - Full format: "App_0115"
 *   - Hex format: "0x0115"
 *
 * @param {string} varMinCompat - e.g. "0115|0115", "App_0706", "0x0706"
 * @param {string} xmc4Variant - explicit XMC4 variant (optional, used if varMinCompat has no pipe)
 * @returns {string}
 */
export const formatEcuAppVariants = (varMinCompat, xmc4Variant) => {
  if (!varMinCompat) return ''

  const formatSuffix = (suffix) => {
    if (!suffix) return null
    if (suffix.startsWith('App_')) return suffix
    if (suffix.startsWith('0x')) return `App_${suffix.slice(2)}`
    // Raw hex suffix (e.g., "0115")
    return `App_${suffix}`
  }

  // Handle pipe-separated format: "0115|0115" or "App_0115|App_0115"
  if (varMinCompat.includes('|')) {
    const [xmc3Raw, xmc4Raw] = varMinCompat.split('|')
    const xmc3Display = formatSuffix(xmc3Raw)
    const xmc4Display = formatSuffix(xmc4Raw)
    const parts = []
    if (xmc3Display) parts.push(`XMC 3: ${xmc3Display}`)
    if (xmc4Display) parts.push(`XMC 4: ${xmc4Display}`)
    return parts.join('    ')
  }

  // Single value: treat as XMC 3, use explicit xmc4Variant if provided
  const parts = []
  const xmc3Formatted = formatSuffix(varMinCompat)
  if (xmc3Formatted) {
    parts.push(`XMC 3: ${xmc3Formatted}`)
  }
  const xmc4Formatted = formatSuffix(xmc4Variant)
  if (xmc4Formatted) {
    parts.push(`XMC 4: ${xmc4Formatted}`)
  }
  return parts.join('    ')
}
